import { useQuery } from "react-query";
import axios from "axios";
import { useAuthContext } from "contexts/auth";

export type HeritageCatalogDownloadStatus = {
  inProgress: boolean;
  syncKind?: HeritageCatalogSyncKind | null;
  lastSyncedAt: string | null;
  itemCount: number;
};

export type HeritageCatalogSyncKind = "branch" | "purchase_history";

export function heritageCatalogDownloadStatusQueryKey(companyId?: number | null) {
  return ["heritage-catalog-download-status", companyId ?? null] as const;
}

type Options = {
  enabled?: boolean;
  refetchInterval?: number | false;
};

/** Company-scoped catalog download status — avoids cross-tenant cache bleed in React Query. */
export function useHeritageCatalogDownloadStatus(options: Options = {}) {
  const auth = useAuthContext();
  const companyId = auth?.company_id ?? null;
  const baseEnabled = Boolean(companyId);
  const enabled = baseEnabled && (options.enabled !== false);

  return useQuery<HeritageCatalogDownloadStatus>(
    heritageCatalogDownloadStatusQueryKey(companyId),
    () =>
      axios
        .get<HeritageCatalogDownloadStatus>(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/catalog-download-status`
        )
        .then((res) => res.data),
    {
      enabled,
      staleTime: 0,
      refetchInterval: enabled ? options.refetchInterval ?? false : false,
    }
  );
}

export function invalidateHeritageCatalogDownloadStatus(
  queryClient: { invalidateQueries: (key: readonly unknown[]) => Promise<unknown> },
  companyId?: number | null
) {
  return queryClient.invalidateQueries(heritageCatalogDownloadStatusQueryKey(companyId));
}
